import filter from 'lodash/filter';
import get from 'lodash/get';
import includes from 'lodash/includes';
import sortBy from 'lodash/sortBy';
import React from 'react';

import CustomAttributesEditor from './CustomAttributesEditor';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import customAttributesResource from 'src/actions/customAttributes/customAttributesResource';
import useResource, { isLoadingStatus } from 'src/presentation/hooks/useResource';
import useRouter from 'src/presentation/hooks/useRouter';
import RowActions from 'src/presentation/components/pages/customAttributes/RowActions';

const dataAidPrefix = 'admin-custom-attributes';
const ACTIVE_COLUMN_CONFIG = [
  { attr: 'label', label: 'Display Name', customColumnStyle: () => ({ width: '250px' }) },
  { attr: 'key', label: 'Key' },
];
const ARCHIVED_COLUMN_CONFIG = [
  ...ACTIVE_COLUMN_CONFIG,
  {
    attr: 'updatedAt',
    label: 'Archived On',
    customRenderer: item => {
      return new Date(item.updatedAt).toLocaleString('default', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
  },
];

enum TABS {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}
const tabConfig = [
  {
    columns: ACTIVE_COLUMN_CONFIG,
    label: 'Active',
    key: TABS.ACTIVE,
    filter: customAttribute => !customAttribute.deactivated,
    filterPlaceholder: 'Search Active Freeform Topics',
  },
  {
    columns: ARCHIVED_COLUMN_CONFIG,
    label: 'Archived',
    key: TABS.ARCHIVED,
    filter: customAttribute => customAttribute.deactivated,
    filterPlaceholder: 'Search Archived Freeform Topics',
  },
];
const allowedTabHashes = [TABS.ACTIVE.toLowerCase(), TABS.ARCHIVED.toLowerCase()];

const CustomAttributes: TablePageRender = ({ root }) => {
  const { values: customAttributes, loadingState, errors } = useResource(customAttributesResource);
  const { location } = useRouter();

  const actions = (
    <RouterLinkButton data-aid={`${dataAidPrefix}-addNew`} to={`${root}/new`}>
      Create Freeform Topic
    </RouterLinkButton>
  );

  const description = (
    <span>
      Freeform Topics add more context and insight to Conversations in Gladly. To learn more about Freeform Topics,
      visit{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://help.gladly.com/docs/what-is-freeform-topics/">
        Help Docs
      </a>
      .
    </span>
  );

  return (
    <TablePage
      actions={actions}
      data-aid={`${dataAidPrefix}-page`}
      description={description}
      editor={CustomAttributesEditor}
      errors={errors}
      filterFunc={customFilter}
      isLoading={isLoadingStatus(loadingState)}
      items={sortCustomAttributes(customAttributes)}
      name="Freeform Topics"
      preselectedTab={getCurrentTab(location)}
      renderRowActions={props => <RowActions {...props} />}
      resetErrors={customAttributesResource.actions.resetErrors}
      root={root}
      tabConfig={tabConfig}
      updateUrlOnTabChange
    />
  );
};

function sortCustomAttributes(customAttributes) {
  return sortBy(customAttributes, item => item.label.toLowerCase());
}

function customFilter(allCustomAttrs, searchString, selectedTab) {
  const tabFilteredAttributes = filter(allCustomAttrs, customAttr => {
    const isCustomAttributeArchived = customAttr.deactivated;
    return selectedTab === 'Archived' ? isCustomAttributeArchived : !isCustomAttributeArchived;
  });

  if (searchString) {
    const normalizedSearchString = searchString.trim().toLowerCase();
    const labelMatch = attr => attr.label.toLowerCase().includes(normalizedSearchString) as boolean;
    const keyMatch = attr => attr.key.toLowerCase().includes(normalizedSearchString) as boolean;

    return filter(tabFilteredAttributes, attr => {
      return labelMatch(attr) || keyMatch(attr);
    });
  } else {
    return tabFilteredAttributes;
  }
}

function getCurrentTab(location) {
  const locationHash = get(location, 'hash', '');
  const SELECTED_TAB_WITH_FILTER_REGEX = /^#(?<currentTab>\w+)?(\?search=(?<search>[\w+.-]+))?/i;
  const hashParams = get(locationHash.toLowerCase().match(SELECTED_TAB_WITH_FILTER_REGEX), 'groups', {});
  let tabFromHash = get(hashParams, 'currentTab', '');
  if (tabFromHash && !includes(allowedTabHashes, tabFromHash)) {
    tabFromHash = TABS.ACTIVE;
  }
  return tabFromHash;
}

export default CustomAttributes;
